import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { ProfilePictureService } from 'src/core/services/profile-picture.service';
import { AuthStore } from 'src/core/stores';
import {
  CoreUserProfileService,
  UserDto,
} from 'src/generated/api/nestjs-backend';
import { ImageCroppingComponent } from '../image-cropping/image-cropping.component';

//TODO Remove/Refactor the hardcoded image format and image file size
const VALID_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/jpg',
];
const MAX_IMAGE_SIZE_MB = 5;

@Component({
  selector: 'core-avatar',
  templateUrl: './avatar.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ImageCroppingComponent],
})
export class AvatarComponent implements OnInit, OnDestroy {
  private coreUserProfileService = inject(CoreUserProfileService);
  private cdr = inject(ChangeDetectorRef);
  private profilePictureService = inject(ProfilePictureService);
  private logger = inject(NGXLogger);
  authStore = inject(AuthStore);

  @ViewChild('fileInput') fileInput: ElementRef | undefined;
  @ViewChild(ImageCroppingComponent) imageCropper:
    | ImageCroppingComponent
    | undefined;
  @Input() hasUploadButton: boolean = false;
  @Input() user: UserDto | undefined;
  @Input() userProfileImage: string | undefined | null;
  @Input() circleClasses: string = 'w-10';
  @Input() textClasses: string = '';

  private profilePictureSubscription: Subscription | undefined;
  showCropper: boolean = false;

  constructor() {
    if (!this.user) {
      this.user = this.authStore.currentUser();
    }
    if (!this.userProfileImage && this.user?.profileImageUuid) {
      this.getProfileAvatar();
    }
  }

  ngOnInit() {
    this.profilePictureSubscription =
      this.profilePictureService.profilePicture$.subscribe((picture) => {
        this.userProfileImage = picture;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    if (this.profilePictureSubscription) {
      this.profilePictureSubscription.unsubscribe();
    }
  }

  getProfileAvatar(): Subscription {
    return this.coreUserProfileService
      .getUserProfileImage({ xTenantSlug: this.authStore.tenantSlug() })
      .subscribe((response: Blob) => {
        this.userProfileImage = URL.createObjectURL(response);
        this.profilePictureService.setProfilePicture(this.userProfileImage);
        this.cdr.detectChanges();
      });
  }

  changeProfilePicture(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement && inputElement.files && inputElement.files.length > 0) {
      const file: File = inputElement.files[0];

      if (!this.isValidImage(file)) {
        return;
      }

      this.showCropper = true;
      this.imageCropper?.openModal(event);
    }
  }

  onModalClosed() {
    this.showCropper = false;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onImageProcessed(croppedFile: File) {
    this.uploadProfilePicture(croppedFile);
  }

  private isValidImage(file: File): boolean {
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      alert('Only JPG, JPEG, PNG, and WEBP files are allowed.');
      return false;
    }

    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return false;
    }

    return true;
  }

  private uploadProfilePicture(file: File) {
    this.coreUserProfileService
      .createUserProfileImage({
        xTenantSlug: this.authStore.tenantSlug(),
        file,
      })
      .subscribe({
        next: () => {
          this.authStore.refresh().subscribe();
          this.getProfileAvatar();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.logger.error(
            'Error while setting profile picture: ',
            error.error?.message,
          );
        },
      });
  }
}
